import {
  type Instant,
  type LocalDate,
  type Money,
  type Ratio,
  formatLocalDate,
  isCurrencyCode,
  money,
  parseInstant,
  parseLocalDate,
  ratio,
  toApiMoney,
  toApiRatio,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export type TargetMetric =
  'ACCOUNT_BALANCE' | 'NET_WORTH' | 'MONTHLY_SAVINGS' | 'EXPENSE_INCOME_RATIO' | 'LIABILITY_ASSET_RATIO'

export const MONEY_METRICS: readonly TargetMetric[] = ['ACCOUNT_BALANCE', 'NET_WORTH', 'MONTHLY_SAVINGS']
export const RATIO_METRICS: readonly TargetMetric[] = ['EXPENSE_INCOME_RATIO', 'LIABILITY_ASSET_RATIO']

export function isMoneyMetric(metric: TargetMetric): boolean {
  return MONEY_METRICS.includes(metric)
}

export type TargetStatus = 'MET' | 'NOT_MET' | 'NO_DATA'

/**
 * A metric value self-describes whether it is money or a ratio, so a caller never has to
 * re-derive that from the target's own `metric` - the one place that mapping happens is
 * {@link toMetricValue}.
 */
export type MetricValue =
  { readonly kind: 'money'; readonly value: Money } | { readonly kind: 'ratio'; readonly value: Ratio }

export interface TargetEvaluation {
  readonly asOf: LocalDate
  readonly status: TargetStatus
  readonly currentValue: MetricValue | undefined
  readonly goalDatePassed: boolean
}

export interface Target {
  readonly id: string
  readonly name: string
  readonly metric: TargetMetric
  readonly accountId: string | undefined
  readonly currency: string | undefined
  readonly minValue: MetricValue | undefined
  readonly maxValue: MetricValue | undefined
  readonly goalDate: LocalDate | undefined
  readonly evaluation: TargetEvaluation
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface CreateTargetInput {
  readonly name: string
  readonly metric: TargetMetric
  readonly accountId?: string
  readonly currency?: string
  readonly minValue?: MetricValue
  readonly maxValue?: MetricValue
  readonly goalDate?: LocalDate
}

export const targetKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'targets', 'list'),
  detail: (ledgerId: string, targetId: string): CacheKey =>
    ledgerKey(ledgerId, 'targets', 'detail', targetId),
  /** Everything a write to a target (creation, deletion) makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'targets')],
} as const

export interface TargetsRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly Target[]>
  get(ledgerId: string, targetId: string, signal?: AbortSignal): Promise<Target>
  create(ledgerId: string, input: CreateTargetInput): Promise<Target>
  delete(ledgerId: string, targetId: string): Promise<void>
}

function apiValue(value: MetricValue): string {
  return value.kind === 'money' ? toApiMoney(value.value).amount : toApiRatio(value.value)
}

/**
 * `@taler/domain`'s `CurrencyCode` is the full ISO 4217 list; the contract's currency enum
 * is only what the server accepts today, so the wire type is narrower - the same type gap
 * documented on `loans.ts`'s own `apiMoney`.
 */
function apiCurrency(value: string): NonNullable<Schemas['CreateTargetRequest']['currency']> {
  return value as NonNullable<Schemas['CreateTargetRequest']['currency']>
}

export function createTargetsRepository(client: TalerClient): TargetsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/targets`
  const one = (ledgerId: string, targetId: string) => `${base(ledgerId)}/${encodeURIComponent(targetId)}`

  return {
    async list(ledgerId, signal) {
      const targets = await client.call<Schemas['TargetResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return targets.map(toTarget)
    },

    async get(ledgerId, targetId, signal) {
      return toTarget(
        await client.call<Schemas['TargetResponse']>({
          method: 'GET',
          path: one(ledgerId, targetId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      const body: Schemas['CreateTargetRequest'] = { name: input.name, metric: input.metric }
      if (input.accountId !== undefined) body.accountId = input.accountId
      if (input.currency !== undefined) body.currency = apiCurrency(input.currency)
      if (input.minValue !== undefined) body.minValue = apiValue(input.minValue)
      if (input.maxValue !== undefined) body.maxValue = apiValue(input.maxValue)
      if (input.goalDate !== undefined) body.goalDate = formatLocalDate(input.goalDate)
      return toTarget(
        await client.call<Schemas['TargetResponse'], Schemas['CreateTargetRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body,
        }),
      )
    },

    async delete(ledgerId, targetId) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, targetId) })
    },
  }
}

/**
 * The one place a raw wire value (a bound, or the evaluation's `currentValue`) becomes a
 * typed domain value - money in the target's own currency for the three amount metrics,
 * a currency-free {@link Ratio} for the two percentage metrics. A money metric with no
 * currency has nothing to parse the amount into, so it comes back `undefined` rather than
 * guessing a currency - the same "no evaluation currency available" gap the server itself
 * represents by omitting the field.
 */
function toMetricValue(
  raw: string | undefined,
  metric: TargetMetric,
  currency: string | undefined,
): MetricValue | undefined {
  if (raw === undefined) return undefined
  if (isMoneyMetric(metric)) {
    if (currency === undefined || !isCurrencyCode(currency)) return undefined
    return { kind: 'money', value: money(raw, currency) }
  }
  return { kind: 'ratio', value: ratio(raw) }
}

function toTarget(response: Schemas['TargetResponse']): Target {
  return {
    id: response.id,
    name: response.name,
    metric: response.metric,
    accountId: response.accountId,
    currency: response.currency,
    minValue: toMetricValue(response.minValue, response.metric, response.currency),
    maxValue: toMetricValue(response.maxValue, response.metric, response.currency),
    goalDate: response.goalDate === undefined ? undefined : parseLocalDate(response.goalDate),
    evaluation: {
      asOf: parseLocalDate(response.evaluation.asOf),
      status: response.evaluation.status,
      currentValue: toMetricValue(response.evaluation.currentValue, response.metric, response.currency),
      goalDatePassed: response.evaluation.goalDatePassed,
    },
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}
